/**
 * MFN evaluation orchestration — build doc §4.2 Flow B, rules mode.
 * Gathers the fund's MFN clauses, active provisions, and commitments, runs the
 * engine, and persists new triggers (existing clause/provision pairs are kept,
 * preserving their review state).
 *
 * `gatherEvaluateInsertMfn` is the single implementation of that gather/
 * evaluate/insert cycle — both the plain manual-run wrapper below
 * (`runMfnEvaluation`) and the continuous-enforcement orchestrator
 * (`enforceMfnForFund` in `./continuous-mfn.ts`) call it. There is exactly one
 * place that talks to the engine and persists triggers.
 */

import {
  evaluateMfn,
  type CandidateProvision,
  type MfnClauseInput,
  type MfnTriggerResult,
} from "@argus/engine";
import { listMfnClausesByFund, insertMfnTriggers } from "@/lib/api/mfn";
import { listProvisionsByFund } from "@/lib/api/provisions";
import { commitmentMap, listCommitmentsByFund } from "@/lib/api/commitments";

export interface MfnRunSummary {
  clauseCount: number;
  candidateCount: number;
  computedTriggers: number;
  newTriggers: number;
}

/** Gather+evaluate+insert result, extended with the raw computed triggers so
 * callers (continuous-mfn.ts) can diff them against triggers already on file
 * without re-running the engine a second time. */
export interface MfnGatherEvaluateInsertResult extends MfnRunSummary {
  triggers: MfnTriggerResult[];
}

export async function gatherEvaluateInsertMfn(
  tenantId: string,
  fundId: string,
): Promise<MfnGatherEvaluateInsertResult> {
  const [clauseRows, provisionRows, commitmentRows] = await Promise.all([
    listMfnClausesByFund(tenantId, fundId),
    listProvisionsByFund(tenantId, fundId, { activeOnly: true }),
    listCommitmentsByFund(tenantId, fundId),
  ]);

  const clauses: MfnClauseInput[] = clauseRows.map((c) => ({
    id: c.id,
    holderLpId: c.lp_id,
    scope: c.scope,
    thresholdCommitmentUsd:
      c.threshold_commitment_usd !== null ? Number(c.threshold_commitment_usd) : null,
    excludedCategories: c.excluded_categories ?? [],
    fundScope: c.fund_scope,
    sizeBased: Boolean((c.scope_details as { size_based?: boolean } | null)?.size_based),
  }));

  const candidates: CandidateProvision[] = provisionRows.flatMap((p) =>
    p.lp_id === null
      ? []
      : [
          {
            id: p.id,
            lpId: p.lp_id,
            category: p.category,
            structuredValue: p.structured_value,
            summary: p.summary,
          },
        ],
  );

  const triggers = evaluateMfn({
    clauses,
    provisions: candidates,
    commitments: commitmentMap(commitmentRows),
  });

  const inserted = await insertMfnTriggers(
    triggers.map((t) => ({
      tenant_id: tenantId,
      mfn_clause_id: t.mfnClauseId,
      triggering_provision_id: t.triggeringProvisionId,
      fund_id: fundId,
      holder_lp_id: t.holderLpId,
      beneficiary_lp_id: t.beneficiaryLpId,
      category: t.category,
      rationale: t.rationale,
      confidence: t.confidence,
    })),
  );

  return {
    clauseCount: clauses.length,
    candidateCount: candidates.length,
    computedTriggers: triggers.length,
    newTriggers: inserted,
    triggers,
  };
}

/**
 * Back-compat wrapper preserving the pre-continuous-enforcement signature and
 * behavior exactly. Every UI call site now goes through `enforceMfnForFund`
 * (continuous-mfn.ts) instead, which layers the append-only evaluation log and
 * stale-trigger diffing on top of this same gather/evaluate/insert core; this
 * export remains for direct programmatic use (scripts, evals) that only need
 * the run summary.
 */
export async function runMfnEvaluation(tenantId: string, fundId: string): Promise<MfnRunSummary> {
  const { clauseCount, candidateCount, computedTriggers, newTriggers } =
    await gatherEvaluateInsertMfn(tenantId, fundId);
  return { clauseCount, candidateCount, computedTriggers, newTriggers };
}
